import React, { createContext, useContext, useState, useEffect } from 'react';

const EnrollmentCartContext = createContext();

export function useEnrollmentCart() {
  const context = useContext(EnrollmentCartContext);
  if (!context) {
    throw new Error('useEnrollmentCart must be used within EnrollmentCartProvider');
  }
  return context;
}

export function EnrollmentCartProvider({ children }) {
  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem('enrollment_cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('enrollment_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, coverageLevel, premium) => {
    const item = {
      id: `${product.id}_${coverageLevel}`,
      product_id: product.id,
      product_name: product.name,
      product_type: product.benefit_type,
      coverage_level: coverageLevel,
      monthly_premium: premium,
      coverage_amount: product.coverage_amount,
      requires_health_questions: product.requires_health_questions,
      added_at: new Date().toISOString()
    };
    
    setCartItems(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) {
        return prev.map(i => i.id === item.id ? item : i);
      }
      return [...prev, item];
    });
  };

  const removeFromCart = (itemId) => {
    setCartItems(prev => prev.filter(item => item.id !== itemId));
  };

  const updateCartItem = (itemId, updates) => {
    setCartItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, ...updates } : item
    ));
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('enrollment_cart');
  };

  const getTotalPremium = () => {
    return cartItems.reduce((sum, item) => sum + item.monthly_premium, 0);
  };

  const isInCart = (productId) => {
    return cartItems.some(item => item.product_id === productId);
  };

  return (
    <EnrollmentCartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      updateCartItem,
      clearCart,
      getTotalPremium,
      isInCart,
      itemCount: cartItems.length
    }}>
      {children}
    </EnrollmentCartContext.Provider>
  );
}